import { inject, effect, signal, computed, untracked, OnInit, HostListener, Component, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, RouterOutlet, NavigationEnd, NavigationStart, NavigationCancel, NavigationCancellationCode, NavigationError, GuardsCheckEnd } from '@angular/router';
import { routeDataOf, type RouteData } from './guards/route-data';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { defer, timer } from 'rxjs';
import { filter, pairwise, retry, take } from 'rxjs/operators';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { UNSAVED_PROMPT_KEY, UnsavedWorkPromptService, UnsavedWorkRegistry } from '@branduals/dialog-guard';
import { reactToCheckSession } from '@branduals/auth-storage';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faWifiSlash, faWifi, faServer, faArrowsRotate, faXmark } from '@fortawesome/pro-duotone-svg-icons';
import { TranslateModule } from '@ngx-translate/core';
import { TopbarComponent } from './components/topbar/topbar.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { FilePreviewComponent } from './components/file-preview/file-preview.component';
import { ShortcutHelpPanelComponent } from './components/shortcut-help/shortcut-help-panel.component';
import { FilePreviewService } from './services/file-preview.service';
import { PreviewPresence } from './services/preview-presence.service';
import { ArticlePreviewComponent, ArticlePreviewService } from './components/article-preview';
import { RelatedArticlesPanelService } from './components/related-articles-panel';
import { CategoryManagerService } from './components/category-manager';
import { AccessPanelService } from './components/access-panel';
import { DrivePickerPanelService } from './components/drive-picker-panel';
import { CloudImportPanelService } from './components/cloud-import-panel/cloud-import-panel.service';
import { AddToCollectionDialogService } from './components/add-to-collection-dialog';
import { ProvenanceDialogService } from './components/provenance-dialog/provenance-dialog.service';
import { ArticleEditorPresence } from './services/article-editor-presence.service';
import { AccessPanelComponent } from './components/access-panel';
import { DrivePickerPanelComponent } from './components/drive-picker-panel';
import { CloudImportPanelComponent } from './components/cloud-import-panel/cloud-import-panel.component';
import { CollectionDockComponent } from './components/collection-dock';
import { CommandPaletteComponent } from './components/command-palette';
import { AddToCollectionDialogComponent } from './components/add-to-collection-dialog';
import { ShareDialogComponent } from './components/share-dialog/share-dialog.component';
import { LicensePickerDialogComponent } from './components/license-picker-dialog/license-picker-dialog.component';
import { ProvenanceDialogComponent } from './components/provenance-dialog/provenance-dialog.component';
import { ComplianceCheckDialogComponent } from './components/compliance-check-dialog/compliance-check-dialog.component';
import { LicenseWarnDialogComponent } from './components/license-warn-dialog/license-warn-dialog.component';
import { DemoBannerComponent } from './components/demo-banner/demo-banner.component';
import { TrialBannerComponent } from './components/trial-banner/trial-banner.component';
import { SubscriptionBannerComponent } from './components/subscription-banner/subscription-banner.component';
import { TemporalBannerComponent } from './components/temporal-banner/temporal-banner.component';
import { TourOverlayComponent } from './components/tour-overlay/tour-overlay.component';
import { ConfigErrorComponent } from './components/config-error/config-error.component';
import { DomainNotConnectedComponent } from './components/config-error/domain-not-connected.component';
import { ToastFacesComponent } from './components/action-toast/toast-faces.component';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { PublicEventsService, EventTypes } from 'angular-auth-oidc-client';
import { TranslateService } from '@ngx-translate/core';
import { LocaleService } from './services/locale.service';
import { routeRebuild } from './route-rebuild';
import { UploadService } from './services/upload.service';
import { AuthService } from './services/auth.service';
import { AppConfigService } from './services/app-config.service';
import { ItemChangeSubscriptionService } from './services/item-change-subscription.service';
import { CollectionChangeSubscriptionService } from './services/collection-change-subscription.service';
import { PermissionChangeSubscriptionService } from './services/permission-change-subscription.service';
import { MembershipChangeSubscriptionService } from './services/membership-change-subscription.service';
import { SubscriptionChangeSubscriptionService } from './services/subscription-change-subscription.service';
import { TierService } from './services/tier.service';
import { NotificationService } from './services/notification.service';
import { MembershipService } from './services/membership.service';
import { ShareArrivalService } from './services/share-arrival.service';
import { BootParamStripper } from './services/boot-param-stripper.service';
import { DemoModeService } from './services/demo-mode.service';
import { AnalyticsCollectorService } from './services/analytics-collector.service';
import { PublicAnalyticsCollectorService } from './services/public-analytics-collector.service';
import { ConnectivityService } from './services/connectivity.service';
import { VersionCheckService } from './services/version-check.service';
import { PreferencesService } from './services/preferences.service';
import { TemporalContextService, TemporalNavTrigger } from './services/temporal-context.service';
import { CacheResetService } from './services/cache-reset.service';
import { SystemAccessService } from './services/system-access.service';
import { PausedRedirectService } from './services/paused-redirect.service';
import { PublicModeConfigRefreshService } from './services/public-mode-config-refresh.service';
import { MovedHostService } from './services/moved-host.service';
import { AppBodyOffsetDirective } from './directives/app-body-offset.directive';
import { moduleFromUrl } from './utils/module-from-url';
import { hasSsoHandoff, hasSsoHint, SSO_HANDOFF_PARAM, withoutSsoHandoff } from './utils/sso-hint.utils';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, ToastModule, ConfirmDialogModule, ProgressSpinnerModule, FontAwesomeModule, TranslateModule, TopbarComponent, SidebarComponent, FilePreviewComponent, ShortcutHelpPanelComponent, ArticlePreviewComponent, AccessPanelComponent, DrivePickerPanelComponent, CloudImportPanelComponent, CollectionDockComponent, CommandPaletteComponent, AddToCollectionDialogComponent, ShareDialogComponent, LicensePickerDialogComponent, ProvenanceDialogComponent, ComplianceCheckDialogComponent, LicenseWarnDialogComponent, DemoBannerComponent, TrialBannerComponent, SubscriptionBannerComponent, TemporalBannerComponent, TourOverlayComponent, ConfigErrorComponent, DomainNotConnectedComponent, ToastFacesComponent, AppBodyOffsetDirective],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit {
  /** The leave prompt's keyed mount rides the root ConfirmationService (see the template). */
  protected readonly UNSAVED_PROMPT_KEY = UNSAVED_PROMPT_KEY;

  private readonly titleService = inject(Title);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  protected readonly localeService = inject(LocaleService);
  private readonly uploadService = inject(UploadService);
  private readonly appConfigService = inject(AppConfigService);
  private readonly itemChangeSubscription = inject(ItemChangeSubscriptionService);
  private readonly collectionChangeSubscription = inject(CollectionChangeSubscriptionService);
  private readonly permissionChangeSubscription = inject(PermissionChangeSubscriptionService);
  private readonly membershipChangeSubscription = inject(MembershipChangeSubscriptionService);
  private readonly subscriptionChangeSubscription = inject(SubscriptionChangeSubscriptionService);
  private readonly tierService = inject(TierService);
  private readonly notificationService = inject(NotificationService);
  private readonly membershipService = inject(MembershipService);
  private readonly shareArrival = inject(ShareArrivalService);
  private readonly bootParams = inject(BootParamStripper);
  private readonly demoModeService = inject(DemoModeService);
  private readonly analytics = inject(AnalyticsCollectorService);
  private readonly publicAnalytics = inject(PublicAnalyticsCollectorService);
  private readonly temporalContext = inject(TemporalContextService);
  private readonly cacheReset = inject(CacheResetService);
  private readonly filePreviewService = inject(FilePreviewService);
  private readonly articlePreviewService = inject(ArticlePreviewService);
  private readonly relatedArticlesPanelService = inject(RelatedArticlesPanelService);
  private readonly categoryManagerService = inject(CategoryManagerService);
  private readonly accessPanelService = inject(AccessPanelService);
  private readonly drivePickerPanelService = inject(DrivePickerPanelService);
  private readonly cloudImportPanelService = inject(CloudImportPanelService);
  private readonly addToCollectionDialogService = inject(AddToCollectionDialogService);
  private readonly provenanceDialogService = inject(ProvenanceDialogService);
  // The one read the shell has of the full-screen article editor: its presence home, never the editor itself (which
  // would ride in every page's first bundle).
  private readonly articleEditorPresence = inject(ArticleEditorPresence);
  // The ONE "a full-screen preview owns the keyboard" read — the pages' key handlers ride it too.
  private readonly previewPresence = inject(PreviewPresence);
  /** Trigger of the in-flight navigation — pairs NavigationStart with the NavigationEnd tap below. */
  private lastNavTrigger: TemporalNavTrigger = 'imperative';
  /** This boot arrived through a signed-in platform hop carrying the hint as `sso=1` (see consumeSsoHandoff). */
  private ssoHandoff = false;
  private readonly destroyRef = inject(DestroyRef);
  protected readonly connectivity = inject(ConnectivityService);
  protected readonly versionCheck = inject(VersionCheckService);
  private readonly eventService = inject(PublicEventsService);
  private readonly authService = inject(AuthService);
  private readonly preferencesService = inject(PreferencesService);
  private readonly systemAccess = inject(SystemAccessService);
  // Constructed with the shell so the mid-session access-denial catch is armed from boot.
  private readonly pausedRedirect = inject(PausedRedirectService);
  // Constructed with the shell too: a public brand's visitor whose brand turns its public access off meets a
  // refusal on every module read — this service re-reads the tenant config on the client's refusal notification.
  private readonly publicModeConfigRefresh = inject(PublicModeConfigRefreshService);
  // Constructed with the shell too: an app open on a brand's own domain when that host stops serving the
  // brand is answered brand-not-found on every call — this service re-reads the config and moves the tab.
  private readonly movedHost = inject(MovedHostService);
  // The asks before a rebuild (the constructor's effects — the reverse flip's, the lens switch's): the registry's
  // armed-window and unsaved-work reads, and the leave prompt.
  private readonly unsavedWork = inject(UnsavedWorkRegistry);
  private readonly leavePrompt = inject(UnsavedWorkPromptService);

  protected readonly faWifiSlash = faWifiSlash;
  protected readonly faWifi = faWifi;
  protected readonly faServer = faServer;
  protected readonly faArrowsRotate = faArrowsRotate;
  protected readonly faXmark = faXmark;

  private readonly currentUrl = signal('/');
  /** The content language the lens effect last saw — undefined until its first run, so the boot's lens is never a move. */
  private lastContentLens: string | undefined;
  /**
   * The content language the mounted page was READ under: set by every re-run and by every lens move before the
   * router's first navigation (the initial reads take the lens they find). A lens that differs from it is a switch
   * still OWED — declined over unsaved work, kept until the member's next page settles it (`settleLensDebt`).
   */
  private pageLens: string | undefined;
  /** A public-surface landing by a signed-in caller whose standing had not settled at the navigation's End — recorded once it does. */
  private heldPublicLanding: string | null = null;
  /** Public mode as the mode effect last saw it — undefined until its first run, so a shell born in public mode never re-runs on its first turn. */
  private lastPublicMode: boolean | undefined;
  /** The admission (with the identity beside it) as the provisioning effect last saw it — undefined until its first run. */
  private lastAdmission: boolean | undefined;

  /** Tenant config failed to load after retries — the shell renders only the error page. */
  protected readonly configLoadFailed = this.appConfigService.configLoadFailed;

  /** Foreign host with no brand behind it (custom domain not connected) — dedicated notice. */
  protected readonly domainNotConnected = this.appConfigService.domainNotConnected;

  // Shell behaviour is driven by the ACTIVE ROUTE's `data` (single source of truth — drift-proof), not by
  // URL-prefix string matching. `hideSidebar` → chromeless public surfaces (portal/share/join/request-access/auth).
  // Initialised to hideSidebar:true so the sidebar stays hidden until the first route resolves (matches prior behaviour).
  // TYPED at its one producer (the deepest route's data read through `routeDataOf`), so every reader below takes a
  // flag by its typed name and a misspelled key is a compile error, never a flag silently never read.
  private readonly _routeData = signal<RouteData>({ hideSidebar: true });
  // In public mode the brand portal at `/` sits INSIDE the shell: the chrome lift is scoped to the one node that
  // carries `shellInPublicMode`; every other chromeless route a visitor can reach stays chromeless.
  readonly showSidebar = computed(() => {
    const data = this._routeData();
    return data.hideSidebar !== true || (data.shellInPublicMode === true && this.membershipService.publicMode());
  });
  /** True on chromeless public routes — used to NOT bounce them to the portal on auth-loss. */
  readonly isAnonymousRoute = computed(() => this._routeData().hideSidebar === true);

  /**
   * A full-screen preview (a file or an article) is open on one of the two ROOT overlays — the ones
   * this shell mounts outside the layout. The app layout behind it goes `inert` meanwhile: the mouse
   * already cannot reach the page under the overlay, and the keyboard must not either — Tab past the
   * overlay's controls, or one Shift+Tab, would otherwise walk the live tiles behind it, where arrows,
   * Enter and Space act on the page under the modal. The root overlays stay live because they sit
   * outside the inert element; the topbar's shortcuts are unreachable meanwhile, by design. The
   * anonymous share and portal pages mount their OWN article overlay inside the router outlet — inside
   * the layout — so it is deliberately NOT read here: keying on it would make that overlay inert too,
   * and the visitor could not close what they had just opened.
   */
  protected readonly previewOverlayOpen = computed(() => this.previewPresence.anyOpen());

  constructor() {
    // React to UI language changes (must be in constructor for injection context)
    effect(() => {
      const lang = this.localeService.activeUILanguage();
      this.translate.use(lang);
    });

    // Update page title reactively when config loads with brand name
    effect(() => {
      const config = this.appConfigService.config$();
      this.titleService.setTitle(config?.brandName ? `${config.brandName} — Branduals` : 'Branduals');
    });

    // THE LENS TRANSITION (public mode): a visitor's visible set follows the content language — the one predicate
    // judges publish per language through the request header — while the content caches are keyed without it. So
    // when the lens moves after the router's first navigation, the shell drops every content cache and re-runs the
    // current URL inside the rebuild window (the routed subtree recreated, the Manual's resolvers re-run): the page
    // re-reads under the new lens — one unpublished in it meets the refusal face, one published in it appears; an
    // open preview keeps its item and re-reads it the same way. A member's caches carry every locale's values and
    // their visible set never moves with the lens on an OFF brand, so nothing runs for them there; on an ON brand an
    // ADMITTED member's visible set FOLLOWS the lens too — the public floor grants them what the public sees, and a
    // denied-floor member's tree is the visitor's — so their switch rebuilds like a visitor's (Robert's ruling of
    // 2026-09-26); a monitoring root's does not (the admission keys the leg, never the root standing). The rebuild
    // recreates the whole routed subtree, so over unsaved work the shell ASKS FIRST, before anything drops (Robert's
    // rulings of 2026-09-26 and 2026-09-30): Leave drops, arms and re-runs; Keep editing changes nothing on screen —
    // the switch stays OWED, and the member's next page settles it (`settleLensDebt`). Before the router's first
    // navigation the boot's own resolution moves the lens (a stored pick, the interface language's follow) and the
    // initial reads take the lens they find — a re-run there would run the empty pre-initial URL and lose the deep
    // link. Tracks the lens ALONE: public mode, the router and the ask are read untracked, so neither the re-run's own
    // navigation nor a mode flip nor a dirty surface's draft signal can re-enter this effect.
    effect(() => {
      const lens = this.localeService.activeContentLanguage();
      untracked(() => {
        const previous = this.lastContentLens;
        this.lastContentLens = lens;
        if (previous === undefined || !this.router.navigated) {
          this.markPageLens(lens);
          return;
        }
        if (previous === lens) return;
        void this.followLens(lens);
      });
    });

    // THE MODE TRANSITIONS — one effect, tracking public mode ALONE: the route data, the router and the ask are
    // read untracked, so neither a re-run's own navigation nor a dirty surface's draft signal can re-enter it. A
    // route match is evaluated per navigation, never reactively, and the router keeps a guard's last answer on a
    // same-URL navigation — so on either transition the shell re-runs the router on the current URL in place, the
    // URL and the history unchanged.
    //
    // THE REVERSE FLIP: public mode turns TRUE while a member's page is active — the session was lost, or the
    // standing changed. FIRST, on every route, the content caches drop: they hold the member view's answers (the
    // tree, the items, the pages), which the now-public principal must never be served from — the mirror of the
    // forward flip's reason. Then by route: on a MEMBER route the member host would stay mounted over a dead
    // session; after the re-run the member area no longer matches, the member host is destroyed and the refusal
    // face lands. On a PUBLIC-READABLE route the page stays the caller's, but re-read as the public view: the shell
    // arms the rebuild window and re-runs the URL, and the door's guard admits the visitor at its first seat. A
    // chromeless page and the face itself move nothing more. With unsaved work — a member editing on either route
    // class — the shell asks first through the sign-out door's own leave prompt — accept: the one-shot is armed as
    // a belt (the accepted callback's own leave approval is what the table guard reads) and the re-run follows;
    // decline: the member keeps their mounted page with the dead session until they navigate or discard, today's
    // outcome on a decline too, asked once. While a leave approval is already ARMED — the sign-out door asked and
    // was answered — the shell asks nothing and re-runs at once: a second ask over an answer already given would
    // reset the door's window to one End, the re-run's End would clear it before the end-session redirect, and
    // that redirect's beforeunload would then raise the browser's own prompt over the still-registered chrome
    // drafts. The TRANSITION moves it, never the value: a shell born in public mode re-runs nothing on its first
    // turn — the first run only records what it saw.
    //
    // THE FORWARD FLIP: public mode turns FALSE while a PUBLIC page is active — the brand's switch went off under a
    // live visitor (the config re-read learns it on a refused read), or the caller was admitted. The page's next
    // read would be refused, or would serve a member the public view, so the shell drops every content cache (they
    // hold the public view's answers), arms the rebuild window and re-runs the URL: the routed subtree is recreated
    // and — the three public doors carry the window's guard predicate — the door's guard runs again and sends the
    // caller where the shipped guards send them (the brand portal for a visitor of an OFF brand — its face keyed on
    // the portal's own published flag, never on the switch — and their own page for a new member). The TRANSITION
    // moves it, never the value: the first run only records what it saw (a shell born in public mode re-runs nothing);
    // a member route is the face host's own re-match; a chromeless page (the brand portal) serves by its own published
    // flag, so the ending changes nothing it shows; before the router's first
    // navigation nothing runs (the lens effect's boundary — a re-run there would run the empty pre-initial URL and lose
    // the deep link).
    effect(() => {
      const publicMode = this.membershipService.publicMode();
      untracked(() => {
        const previous = this.lastPublicMode;
        this.lastPublicMode = publicMode;
        const data = this._routeData();
        if (publicMode) {
          if (previous !== false) return;
          this.cacheReset.resetContentCaches();
          // The identity-bearing state a lapse must not carry into public mode (Robert's ruling of 2026-09-26): the
          // access cache — a member's system permissions never outlive the session that held them — and the hidden
          // content languages, a lens a member could preview that is no visitor's. The identity-free catalogs a
          // sign-out's full reset also drops (the member list, the hub, the portal, the brand defaults) have no reader
          // on the public routes and stay.
          this.systemAccess.clearCache();
          this.localeService.clearHiddenContentLanguages();
          if (data.hideSidebar === true || data.publicRefusal === true) return;
          const rerun = () => {
            if (data.publicReadable === true) routeRebuild.arm();
            this.rerunCurrentUrl();
          };
          if (this.unsavedWork.leaveApprovalArmed()) {
            rerun();
            return;
          }
          void this.leavePrompt.confirmLeaveIfDirty(() => {
            this.unsavedWork.approveNavigationOnce();
            rerun();
          });
          return;
        }
        if (previous !== true || data.publicReadable !== true || !this.router.navigated) return;
        this.cacheReset.resetContentCaches();
        routeRebuild.arm();
        this.rerunCurrentUrl();
      });
    });

    // THE ADMISSION — the one trigger of the access read and of the member provisioning, shared by every boot
    // path. The ordinary boot's checkAuth and the sign-in callback's both flip `authenticated`: the shell asks for
    // the access read on that signal, and provisions on the ADMISSION the read lands — member or root, the named
    // composition the app-entry guards admit on; access-keyed, never standing-keyed, so a monitoring root with no
    // membership row stays provisioned, and a paused member or a true non-member (seated at None) skips calls the
    // server would refuse. Until 2026-09-29 the provisioning was a one-shot inside syncWithAuthServer, which the
    // callback route skips (ngOnInit returns before it so the callback component exchanges the code alone) — so
    // every document that began with a sign-in ran without its six app-wide streams, the tier state and the bell
    // until a reload; the sidebar fails open on an unloaded tier, so nothing looked wrong, and the missing streams
    // surfaced only as a "Reconnecting…" bar over a socket nothing wanted. The TRANSITION provisions, never the
    // value: an access re-read that keeps the admission (a permission push, a later read reporting the root posture
    // too) provisions nothing twice — the subscribe doors are idempotent, the tier read is not. Teardown stays with
    // its two doors (the sign-out reset, the paused catch): a lost admission releases nothing here.
    effect(() => {
      if (!this.authService.authenticated()) return;
      untracked(() => { void this.systemAccess.ensureLoaded(); });
    });
    effect(() => {
      const admitted = this.authService.authenticated()
        && (this.systemAccess.isMember() || this.systemAccess.isRoot());
      // The bell keys on the identity, and the provisioning is one gesture — so it waits for both.
      const userId = this.authService.user()?.sub ?? null;
      untracked(() => {
        const previous = this.lastAdmission;
        const provisionable = admitted && userId !== null;
        this.lastAdmission = provisionable;
        if (!provisionable || previous === true) return;
        this.provisionMemberSession(userId);
      });
    });

    // A landing held at its End for a standing still loading (the page-view arm below): recorded on the public
    // collector once the standing settles at a non-admitted one — a paused member, a signed-in non-member — and
    // dropped when it settles at admitted or at a root (the member collector keeps its own admission rule).
    effect(() => {
      const state = this.membershipService.membershipState();
      untracked(() => {
        const module = this.heldPublicLanding;
        if (module === null || !state.loaded) return;
        this.heldPublicLanding = null;
        if (!state.isRoot && state.standing !== 'active') this.publicAnalytics.pageViewed(module);
      });
    });

    // THE AUTH-LOSS MONITOR installs here, not in ngOnInit: the callback route's early return (ngOnInit,
    // below) once skipped it too, so every document born from a sign-in kept a member page mounted over a
    // dead session instead of the portal bounce — the same class as the provisioning gap above, on the
    // road OUT of the admitted state. It pairs the library's auth stream and reads the route at fire time.
    this.monitorAuthState();

    this.eventService
      .registerForEvents()
      .pipe(filter((notification) => notification.type === EventTypes.CheckSessionReceived))
      .subscribe((value) => {
        // A frame that can never read the session cookie answers "changed" on every poll; the
        // shared guard re-checks once per person and then stops, or each re-check would reload
        // the tab forever (see @branduals/auth-storage's check-session guard).
        if (reactToCheckSession(value.value, this.authService.user()?.sub) === 'recheck') {
          // The session identifier is a fresh random value written on EVERY sign-in —
          // a change means someone signed in or out on this browser, not that THIS
          // session died. Clear local auth state and re-ask the server WHO is signed
          // in: silent recovery when it was this person re-signing-in, the new identity
          // when someone else did, anonymous when everyone signed out. Navigating to
          // the server sign-out here would REVOKE the session another tab just created.
          this.oidcSecurityService.logoffLocal();
          sessionStorage.removeItem('sso_checked');
          // The boot attribution goes with it: until the server answers, this tab must not paint
          // the previous person's cached theme and language for the person who may have replaced them.
          this.preferencesService.forgetBootAttribution();
          this.oidcSecurityService.authorize(undefined, { customParams: { prompt: 'none' } });
        }
      });

  }

  /**
   * Warn user when trying to close the tab/window while uploads are in progress.
   * The browser will show a native confirmation dialog.
   */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): string | undefined {
    if (this.uploadService.activeUploadsCount() > 0) {
      // Setting returnValue triggers the browser's native confirmation dialog
      // The actual message text is ignored by modern browsers for security reasons
      event.returnValue = 'You have uploads in progress. Are you sure you want to leave?';
      return event.returnValue;
    }
    return undefined;
  }

  ngOnInit() {
    // Tenant config never loaded — only the error page renders, so skip everything
    // else: auth sync would redirect users off the error page, and analytics/
    // subscriptions/router wiring would run against a brand the app knows nothing
    // about. Translations still initialize so the error page copy is localized.
    if (this.configLoadFailed()) {
      this.initializeTranslations();
      return;
    }

    // Time travel: every asOf transition clears the CONTENT caches (the registry's
    // content subset) so all cached content stays clock-homogeneous.
    // The generation fence in the services discards any late-arriving writes.
    this.temporalContext.onTransition(() => {
      this.cacheReset.resetContentCaches();
      // Preview overlays close on every clock transition — an open preview shows
      // one instant's content and must not survive into another. Both closes are
      // safe no-ops when nothing is open.
      this.filePreviewService.close();
      this.articlePreviewService.close();
      // Every panel that writes to the brand as it is NOW closes with the instant, like the previews: in another
      // instant its writes are refused. The Manual's two side panels and the three slide panels hold nothing typed
      // and close at once (safe no-ops when nothing is open). The two dialogs and the full-screen article editor can
      // hold typed work, so they are ASKED and answer with their own guarded close: typed work asks first; one
      // mid-save stands down and stays open at the new instant, and nothing asks it again — exactly as their own
      // close button behaves. The editor is mounted on the page body by its service, outside every route, so the
      // screen's rebuild never reaches it; it is asked through its presence home.
      this.relatedArticlesPanelService.close();
      this.categoryManagerService.close();
      this.accessPanelService.close();
      this.drivePickerPanelService.close();
      this.cloudImportPanelService.close();
      this.addToCollectionDialogService.requestClose();
      this.provenanceDialogService.requestClose();
      this.articleEditorPresence.requestClose();
      // The transition navigations merge query params, so a preview's ?item=
      // SURVIVES the clock change and the recreated route subtree's url bridge
      // would synchronously RE-OPEN the overlay just closed. Strip it at the URL;
      // param-only + replaceUrl, so this can't loop the transition (asOf is
      // unchanged and the sync is value-deduped).
      if (this.router.url.includes('item=')) {
        void this.router.navigate([], {
          queryParams: { item: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
          // Anchors in links shared before every article had its own address ride the fragment (the page view
          // still scrolls to them) — the cleanup navigation must not drop them.
          preserveFragment: true,
        });
      }
    });

    // Initialize translation service
    this.initializeTranslations();

    // Record referrer if external
    if (document.referrer && !document.referrer.startsWith(window.location.origin)) {
      this.analytics.recordReferrer(document.referrer, window.location.pathname + window.location.search);
    }

    // ── Share arrival (both app-load legs — the cross-app `shareInviteId=` param and the
    // stored destination) live in ShareArrivalService: it decides now and ACTS on the first
    // NavigationEnd — an eager navigate here would resolve against the EMPTY pre-initial
    // router state and (with replaceUrl) rewrite the deep URL to '/' before the router
    // reads it, losing the delivery.
    this.shareArrival.consumeAppLoadArrival(window.location.pathname, window.location.search);
    this.ssoHandoff = this.consumeSsoHandoff();

    // Track page views on route navigation
    let previousModule = '';
    this.router.events.pipe(
      filter((e): e is NavigationStart => e instanceof NavigationStart),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(event => {
      this.lastNavTrigger = event.navigationTrigger === 'popstate' ? 'popstate' : 'imperative';
    });
    // Time travel EARLY sync (a 2026-07-13 runtime finding): the clock must move BEFORE the router's
    // resolve + activation phases — CustomRouteReuseStrategy recreates the routed subtree on a
    // transition, and the recreated ngOnInit / re-run resolvers read the ambient asOf at that
    // point. GuardsCheckEnd fires after guards pass and before resolve/activation.
    this.router.events.pipe(
      filter((e): e is GuardsCheckEnd => e instanceof GuardsCheckEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(event => {
      // GuardsCheckEnd fires on REFUSED navigations too (shouldActivate=false) — a leave the
      // member just declined at the unsaved-work prompt must not transition the clock, nor settle
      // an owed lens switch under the page that stays.
      if (!event.shouldActivate) return;
      this.settleLensDebt();
      this.temporalContext.syncFromUrlEarly(event.urlAfterRedirects, this.lastNavTrigger);
    });
    // THE REBUILD WINDOW AND A NAVIGATION THAT NEVER ENDS: the window is armed before a navigation and
    // closed at its End (below). A navigation that never ends — the member declined the leave prompt, a
    // guard threw — would leave it open over the page that stayed, and the next ordinary navigation would
    // rebuild the whole subtree for no reason. A cancel that hands over to another navigation keeps it: a
    // guard's redirect and a newer navigation superseding this one continue as that navigation (the
    // registry carries a leave approval through a redirect the same way; a superseding navigation
    // asks again), and its End closes the window.
    this.router.events.pipe(
      filter((e): e is NavigationCancel | NavigationError => e instanceof NavigationCancel || e instanceof NavigationError),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(event => {
      const handsOver = event instanceof NavigationCancel
        && (event.code === NavigationCancellationCode.Redirect || event.code === NavigationCancellationCode.SupersededByNewNavigation);
      if (!handsOver) routeRebuild.clear();
    });
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(event => {
      // Time travel: the URL is the mode's source of truth — the single tap feeds the service
      // (imperative navs re-attach a dropped ?asOf — never under public mode, where the clock is
      // out before any read and this End speaks the ruled sentence once for the clock it dropped;
      // popstate honors the URL).
      this.temporalContext.syncFromUrl(event.urlAfterRedirects, this.lastNavTrigger);
      routeRebuild.clear(); // the rebuild window closes with the navigation, whatever armed it (the clock, the lens, a mode transition)
      this.currentUrl.set(event.urlAfterRedirects);
      this._routeData.set(this.deepestRouteData()); // route-data drives showSidebar / monitorAuthState
      const module = moduleFromUrl(event.urlAfterRedirects);
      // The PUBLIC collector (server-derived session, anonymous mutation) records a landing on the brand's
      // PUBLIC SURFACE alone — the portal's two nodes and the public modules, read off the active route's
      // data — never the share, join, request-access or auth pages a visitor passes through: in public mode
      // for every caller the brand does not admit, and on the chromeless routes for every caller the brand
      // does not admit whatever the switch says (Robert's ruling of 2026-09-29) — anonymous, or signed in
      // with a SETTLED standing that is not admitted (a non-member, a paused member); a standing still
      // loading holds the landing until it settles (the constructor's effect), so no landing is classed by a
      // standing not yet read. A member's landing rides the member collector, byte-identical to before.
      const routeData = this._routeData();
      // A paused portal is no surface: while the brand's public faces are paused (the config's one word) a landing on the
      // portal's two nodes records nothing — the pause's door sends the visitor on, and a landing that still reaches a
      // portal node is a visit to the paused page.
      const publicSurface = routeData.publicReadable === true
        || (routeData.publicPortal === true && this.appConfigService.config$()?.publicFacesPaused !== true);
      const membership = this.membershipService.membershipState();
      const signedIn = this.authService.signedIn();
      const settledNotAdmitted = !signedIn || (membership.loaded && !membership.isRoot && membership.standing !== 'active');
      if (this.demoModeService.isDemo()) {
        // Every demo landing rides the member collector, which on a demo brand sends to the platform's visit counts
        // (never the brand's analytics) — the portal's nodes included, and before the access read lands, so the
        // visit's first page is never lost to a standing still loading.
        this.analytics.pageViewed(module);
        if (previousModule && previousModule !== module) {
          this.analytics.moduleSwitched(previousModule, module);
        }
      } else if (this.membershipService.publicMode()) {
        if (publicSurface) this.publicAnalytics.pageViewed(module);
      } else if (this.isAnonymousRoute() && settledNotAdmitted) {
        if (publicSurface) this.publicAnalytics.pageViewed(module);
      } else if (this.isAnonymousRoute() && !membership.loaded) {
        this.heldPublicLanding = publicSurface ? module : null;
      } else if (this.systemAccess.isMember() || this.systemAccess.isRoot()) {
        // Member recording rides the ACCESS admission: paused members and signed-in
        // non-members are deliberately unrecorded here — their member flushes would only
        // fail auth server-side and be swallowed.
        this.analytics.pageViewed(module);
        if (previousModule && previousModule !== module) {
          this.analytics.moduleSwitched(previousModule, module);
        }
      }
      previousModule = module;
    });

    // Capture the current URL — minus the hint handoff, so the route a sign-in round trip
    // restores never carries it back.
    const currentUrl = withoutSsoHandoff(window.location.pathname + window.location.search);

    // If we're on the auth callback route, let AuthCallbackComponent handle checkAuth exclusively
    // This prevents a race condition where both components try to exchange the auth code.
    // Nothing member-scoped is lost by returning here: the access read and the provisioning
    // follow the `authenticated` signal the callback's own checkAuth flips (the constructor's
    // admission effects), not this method.
    if (currentUrl.includes('/auth/signin-oidc')) {
      return;
    }

    // In demo mode, skip auth server sync entirely — API assigns user identity
    // Also skip WS subscription (no auth for WS, mutations blocked anyway)
    if (this.demoModeService.isDemo()) {
      // Demo is a terminal auth state of its own: no auth sync ever runs here, so the
      // confirmed-anonymous leg below never fires. Preferences must still resolve —
      // that's what applies the content language for the first paint.
      this.preferencesService.loadAnonymousPreferences();
      return;
    }

    // Load brand settings (join policy, etc.) for all users — public data, no auth required
    this.membershipService.loadBrandSettings().subscribe({
      error: (err: unknown) => console.error('[App] Failed to load brand settings:', err)
    });

    // Store intended URL for deep linking (if not portal root)
    if (currentUrl !== '/') {
      sessionStorage.setItem('authRedirectUrl', currentUrl);
    }

    // Run checkAuth IMMEDIATELY (do NOT defer it) so the auth state is populated before the initial
    // navigation runs portalGuard — otherwise an authenticated member is shown the Portal instead of /home
    // (portalGuard reads isAuthenticated$ with take(1) at guard time). Only the UNAUTHENTICATED probe-vs-
    // anonymous decision needs route data, and it's deferred inside syncWithAuthServer (route resolved by then).
    this.syncWithAuthServer();
  }

  /** The route table re-run on the current URL, in place — the same primitive the face's routed host uses for its own re-match. The page re-reads under the lens of the moment. */
  private rerunCurrentUrl(): void {
    this.pageLens = this.localeService.activeContentLanguage();
    void this.router.navigateByUrl(this.router.url, { onSameUrlNavigation: 'reload', replaceUrl: true });
  }

  /** Whether the visible set follows the content language for this caller: a visitor's does; an ADMITTED member's does on an ON brand (the public floor); a member's on an OFF brand and a monitoring root's do not. */
  private lensMovesTheVisibleSet(): boolean {
    return this.membershipService.publicMode()
      || (this.membershipService.isAdmitted() && this.appConfigService.config$()?.publicAccess === true);
  }

  /**
   * The lens moved after the router's first navigation. A caller whose visible set follows it rebuilds — over
   * unsaved work only after the leave prompt (target-blind: the rebuild recreates every navigation-scope surface,
   * the clock transition's own precedent); Keep editing changes nothing and leaves the switch OWED. A switch back
   * to the language the page was read under owes nothing.
   */
  private async followLens(lens: string): Promise<void> {
    if (!this.lensMovesTheVisibleSet()) {
      this.markPageLens(lens);
      return;
    }
    if (lens === this.pageLens) {
      // A switch back to the language the page was read under: nothing is owed any more.
      routeRebuild.settleLensSwitch();
      return;
    }
    if (this.unsavedWork.hasNavigationWork()) {
      if (!(await this.leavePrompt.confirmLeave('route-leave'))) {
        routeRebuild.oweLensSwitch();
        return;
      }
      this.unsavedWork.approveNavigationOnce();
    }
    this.cacheReset.resetContentCaches();
    routeRebuild.arm();
    this.rerunCurrentUrl();
  }

  /**
   * An owed lens switch, settled at the next navigation's GuardsCheckEnd — after the leave guards (a navigation the
   * member declines never reaches here) and before any resolver or page read: the content caches drop and the window
   * opens, so the navigation rebuilds the routed subtree under the lens the member picked ("At her next page"). The
   * router collected the guards and the resolvers to run BEFORE this seat, so the debt is also visible to the routes'
   * own predicate (`routeRebuild.lensSwitchOwed`, read by `guardsRerunInsideRebuildWindow`): the Manual's path
   * resolver re-runs under the new lens instead of handing the recreated page the path it read under the old one.
   */
  private settleLensDebt(): void {
    const lens = this.localeService.activeContentLanguage();
    if (this.pageLens === undefined || this.pageLens === lens) return;
    this.markPageLens(lens);
    if (!this.lensMovesTheVisibleSet()) return;
    this.cacheReset.resetContentCaches();
    routeRebuild.arm();
  }

  /** The page's lens is set — by the boot, a lens that moves nothing visible, or a settled debt — and nothing stays owed. */
  private markPageLens(lens: string): void {
    this.pageLens = lens;
    routeRebuild.settleLensSwitch();
  }

  /** The deepest activated route's `data`, typed — the active route in a child-less root component; the one place the shell reads the router's untyped Data. */
  private deepestRouteData(): RouteData {
    let route = this.router.routerState.root;
    while (route.firstChild) route = route.firstChild;
    return routeDataOf(route.snapshot.data);
  }

  // The legacy-localStorage token purge lives in main.ts: pure debris cleanup of the
  // pre-partition blob (the library never touches localStorage any more — its storage is
  // the partitioned service). The pre-bootstrap ORDERING constraint belongs to
  // hydrateFromPeers, whose answer must land before the library's synchronous storage read.

  /**
   * Check authentication state on app load.
   * checkAuth() validates local tokens and triggers silent renew via refresh token
   * if they're expiring (configured via silentRenew: true, useRefreshToken: true).
   * With no local tokens the probe-vs-anonymous decision is hint-gated (below).
   */
  private syncWithAuthServer(): void {
    this.oidcSecurityService.checkAuth().subscribe(({ isAuthenticated }) => {
      if (isAuthenticated) {
        // Load current user context (populates avatar, membership state). The access read and
        // the brand-member-scoped provisioning are NOT asked for here: they follow the
        // `authenticated` signal this checkAuth flips (the constructor's admission effects),
        // the same road the sign-in callback's checkAuth takes.
        this.membershipService.loadCurrentUser().subscribe({
          error: (err: unknown) => console.error('[App] Failed to load current user:', err)
        });
      } else {
        // Not authenticated — the probe-vs-anonymous decision waits for the first route
        // resolution only so deep-link readiness gates behave; the decision itself keys
        // on the session hint, never on route shape.
        this.resolveUnauthenticatedRoute();
      }
    });
  }

  /**
   * A signed-in platform hop (the Account app opening a brand on its custom domain) appends
   * `sso=1`: the arriving origin has no hint of its own yet, so the parameter IS the hint for
   * this boot. Dropped once the first navigation has run — asking earlier would resolve
   * against the empty pre-initial router state — and through the shared remover, since a
   * share accept arrives carrying its own parameter to drop in the same turn. A forged
   * parameter costs its visitor one declined round trip and reveals nothing.
   */
  private consumeSsoHandoff(): boolean {
    if (!hasSsoHandoff(window.location.search)) return false;
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.bootParams.strip(SSO_HANDOFF_PARAM));
    return true;
  }

  /**
   * After checkAuth confirms no session: HINT-GATED silent probe. The auth server writes a
   * 1-bit "a session exists somewhere" cookie at every sign-in; present + not yet probed
   * this session → ONE silent probe (prompt=none) on ANY route — deep links, share links
   * and the portal included, because the hint replaces the old route-shape gating. Absent →
   * CONFIRMED ANONYMOUS immediately, zero auth-server round-trips (the historical
   * no-spinner guarantee for anonymous visitors). The anonymous leg resolves content
   * language to the brand default — every terminal auth state must apply a content
   * language, else the first paint reads content in the boot guess (a share link once
   * landed on a state that resolved nothing).
   * Waits for the first NavigationEnd if the initial navigation hasn't resolved yet.
   */
  private resolveUnauthenticatedRoute(): void {
    const decide = () => {
      const hinted = this.ssoHandoff || hasSsoHint();
      if (hinted && !sessionStorage.getItem('sso_checked')) {
        sessionStorage.setItem('sso_checked', '1');
        this.oidcSecurityService.authorize(undefined, { customParams: { prompt: 'none' } }); // browser navigates away
      } else {
        this.preferencesService.loadAnonymousPreferences();
      }
    };
    if (this.router.navigated) {
      decide();
    } else {
      this.router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(() => decide());
    }
  }

  /**
   * Load the brand's tier state with a bounded retry. Everything the app reads off the plan —
   * the read-only banner, the limit faces' answers, the plan page — reads TierService, so a silent failure
   * of this single load mis-gates the app until something else refreshes it. A transient failure
   * (the currentTierState query returns an authorization error as HTTP 200, bypassing the 401
   * refresh corridor) would otherwise never recover: no server push arrives when nothing changed.
   * Recovery after a later socket drop is still handled by the reconnect resync in
   * SubscriptionChangeSubscriptionService; this covers the initial load only.
   */
  private refreshTierStateResiliently(): void {
    defer(() => this.tierService.refresh())
      .pipe(
        retry({ count: 3, delay: (_err, attempt) => timer(attempt * 1000) }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        error: () => console.error(
          '[App] Tier state failed to load after retries; gating stays degraded until a subscription push or socket reconnect.'),
      });
  }

  /**
   * The member session's provisioning, one gesture: the brand's plan-tier state (capabilities, quotas,
   * status — the gating and the banners read it), the five brand-wide realtime streams, and the
   * notification pair (the bell's stream + its initial unread count). Runs once per admission, from
   * the constructor's admission effect.
   */
  private provisionMemberSession(userId: string): void {
    this.refreshTierStateResiliently();
    this.subscribeBrandStreams();
    this.notificationService.subscribe(userId);
    this.notificationService.fetchUnreadCount();
  }

  /**
   * The five brand-wide realtime streams (items, collections, permissions, membership, the plan
   * subscription) — cache invalidation and live state for the whole session. Requires a brandId
   * from config.
   */
  private subscribeBrandStreams(): void {
    const brandId = this.appConfigService.getValue('brandId');
    if (brandId) {
      this.itemChangeSubscription.subscribe(brandId);
      this.collectionChangeSubscription.subscribe(brandId);
      this.permissionChangeSubscription.subscribe(brandId);
      this.membershipChangeSubscription.subscribe(brandId);
      this.subscriptionChangeSubscription.subscribe(brandId);
    } else {
      console.warn('[App] No brandId available — real-time subscriptions NOT established');
    }
  }

  /**
   * Monitor authentication state changes. When auth is lost while on a protected route,
   * redirect to portal (/) so the user sees the brand portal instead of a broken half-state.
   */
  private monitorAuthState(): void {
    this.oidcSecurityService.isAuthenticated$.pipe(
      pairwise(),
      filter(([prev, curr]) => prev.isAuthenticated && !curr.isAuthenticated),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      // Auth was lost — redirect to portal ONLY from a protected route, and only outside public mode: on a brand
      // whose public access is on, the reverse flip (the constructor's effect) owns a lost session — the refusal
      // face lands on the page the member was reading, never a bounce. Anonymous/public routes (portal, share,
      // join, request-access, auth-callback) carry data.hideSidebar and must NOT be bounced (a logged-in user
      // may be viewing the public share page). Route-data driven (drift-proof), not URL-prefix matching.
      if (!this.isAnonymousRoute() && !this.membershipService.publicMode()) {
        this.router.navigate(['/']);
      }
    });
  }

  private initializeTranslations(): void {
    // Register the platform UI-language whitelist (from /api/config) — interface languages are
    // user-level and global, never brand-scoped (B1). en-US is appended defensively so the
    // missing-key fallback language is always registered.
    this.translate.addLangs([...new Set([...this.localeService.getSupportedUILanguages(), 'en-US'])]);

    // Default (missing-key fallback) language stays en-US — the canonical, always-complete catalog.
    this.translate.setDefaultLang('en-US');

    // Set initial UI language
    this.translate.use(this.localeService.getActiveUILanguage());
  }
}
